// RGR02 반품조회 — 화면 규칙(순수 함수). hooks/useRgr02Screen.ts 가 소비하고 rules.test.ts 가 검증한다.
import type {
  GetReturnReportItemsReq,
  GetReturnReportSummariesReq,
  ReturnInboundActionResult,
  ReturnReportItem,
  ReturnReportSummary,
} from '@/services/return/returnInquiry/types'
import { DOWNLOAD_PASSWORD_INVALID_MESSAGE } from '@/utils/downloadPassword'
/** MUSINSA 계정의 기본 화주유형(티켓 "MUSINSA = M1P/MUSINSA"). */
export const DEFAULT_SERVICE_TYPE = 'M1P'
/** 상세사유 최소 글자 수 — BE `validateDetailedReason` 과 같다. */
export const REASON_DETAIL_MIN_LENGTH = 5
/** 재전송이 가능한 주문상태(1.0 RGR02.js onResend 동치). */
export const RESENDABLE_STATUS = '입고대기'
/** 이미 취소된 주문상태 — BE `CancelInboundValidation` 이 거절하는 `InboundReportStatus.CANCEL.code`. 섞여 있으면 선택 전체를 막는다. */
export const CANCELED_STATUS = '입고취소'
/**
 * 입고유형 선택지는 반품 하나 — BE 가 `ORD_TYPE = 반품` 을 고정하고 고른 값을 AND 로 겹친다(moms-be ReturnReportJpaRepository
 * `headPredicate`). 데모의 해외·폐기 등은 항상 0건이라 뺀다. '전체'(빈 값)는 뷰가 따로 붙인다.
 */
export const ORD_TYPE_OPTIONS = [{ value: '반품', label: '반품' }]
/** BE 응답에 없는 ITEM 컬럼 표시값. */
const NOT_PROVIDED = '-'

export const MESSAGES = {
  requiredShipper: '화주유형 또는 화주명을 선택해 주세요.',
  searchFailed: '조회에 실패했습니다.',
  itemKeyMissing: '입고번호·마감일자·차수가 없는 입고문서라 상세를 조회할 수 없습니다.',
  cancelKeyMissing: '화주코드·마감일자·차수·입고번호가 없는 입고문서는 취소할 수 없습니다.',
  cancelAlreadyCanceled: '이미 입고취소된 입고문서가 포함되어 있습니다.',
  cancelFailed: '입고취소에 실패했습니다.',
  cancelResult: (success: number, fail: number) => `입고취소 ${success}건 성공, ${fail}건 실패했습니다.`,
  summaryMissing: '원복·재전송에 필요한 요약 정보가 없는 입고문서입니다.',
  resendNotAllowed: '재전송이 불가한 입고문서입니다.',
  actionFailed: '처리에 실패했습니다.',
  actionUnknown: '처리 결과를 확인하지 못했습니다. 재조회 후 상태를 확인해 주세요.',
  saveFailed: '저장에 실패했습니다.',
  excelNoRows: '다운로드할 데이터가 없습니다.',
  singleRowOnly: '한 건만 선택해 주세요.',
  saveNoTarget: '저장할 수 있는 행이 없습니다.',
  downloadValidation: `사유코드를 고르고 상세사유를 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`,
  passwordInvalid: DOWNLOAD_PASSWORD_INVALID_MESSAGE,
  shipperNameNotInType: '선택한 화주명이 화주유형과 맞지 않습니다. 화주명을 다시 선택해 주세요.',
}

/** 생성본 조회영역 값 버스(flRgr02FilterCard)의 모양 — 키는 생성본 filterKey 그대로, '' 는 '전체'. 날짜는 `YYYY-MM-DD`. */
export interface Rgr02FilterValues {
  service_type?: string[]
  shipper_name?: string[]
  PLN_DATE?: [string, string]
  ORD_OPT_NO?: string[]
  ORD_TYPE?: string
  ACT_DATE?: string
  ORD_NO?: string[]
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** '전체'('')·공백을 뺀 목록. 비면 undefined — 요청·URL 에 빈 키를 싣지 않는다. */
export const pickList = (v?: string[]): string[] | undefined => {
  const list = (v ?? []).map((s) => s.trim()).filter(Boolean)
  return list.length ? list : undefined
}

const toYmd = (v?: string): string | undefined => (v && DATE_RE.test(v) ? v.replace(/-/g, '') : undefined)

export const toSummariesReq = (f: Rgr02FilterValues): GetReturnReportSummariesReq => ({
  logisticsServiceType: pickList(f.service_type),
  shipper: pickList(f.shipper_name),
  ordOptNo: pickList(f.ORD_OPT_NO),
  ordNo: pickList(f.ORD_NO),
  plnDateFrom: toYmd(f.PLN_DATE?.[0]),
  plnDateTo: toYmd(f.PLN_DATE?.[1]),
  actDate: toYmd(f.ACT_DATE),
  ordType: f.ORD_TYPE || undefined,
})

/** BE 필수 조건 — 화주명·화주유형 중 하나(없으면 400). */
export const hasShipperScope = (req: { shipper?: string[]; logisticsServiceType?: string[] }): boolean =>
  Boolean(req.shipper?.length || req.logisticsServiceType?.length)

/**
 * 화주 고정 계정의 응답을 계정 화주코드로 한 번 더 거른다.
 * BE 는 화주명으로만 거른다(`ReturnReportJpaRepository.headPredicate` — `shipper.shipperName.in`). 화주명은 유일하지 않아
 * (dev 실측: `29CM_NIKESAMPLE` 4건 등 코드만 다른 동명 화주) 이름만 맞추면 남의 화주코드 행이 섞이고, 그 행이
 * 입고취소·원복·재전송 대상이 된다. HEAD·ITEM 응답의 `sprNm` 이 화주코드다.
 *
 * 계정에 화주코드가 없으면(세션 `userInfo.shipper_code` 가 비는 계정) 거를 기준이 없어 그대로 둔다 —
 * 빈 값으로 거르면 `sprNm` 이 무엇이든 일치하지 않아 표가 통째로 0건이 된다.
 *
 * 재고통합 ON 이어도 훅은 `toShipper` 를 넘기지 않는다 — 잠긴 29CM·INOOINT 가 고른 화주(자기 화주 또는 MUSINSA) 하나로 조회하고
 * BE 도 요청 화주 그대로 찾으므로 그 화주코드로만 거른다(LOGISPRD-15725). `toShipper` 는 여러 코드를 같은 화주로 볼 때 비교할 때만 접는다.
 */
export const keepOwnShipperRows = <T extends { sprNm: string | null }>(
  rows: T[],
  ownShipperCode: string,
  toShipper: (code: string) => string = (code) => code,
): T[] => {
  if (!ownShipperCode) return rows
  const own = toShipper(ownShipperCode)
  return rows.filter((r) => r.sprNm !== null && toShipper(r.sprNm) === own)
}

/**
 * 복원 조건에 입고예정일자가 없으면 기본 기간(오늘)으로 채운다.
 * BE 는 날짜를 선택 조건으로 둬서(`headPredicate` 의 `plnDateFrom`·`plnDateTo` 는 null 이면 조건을 걸지 않는다)
 * 날짜 없이 조회하면 화주 조건만으로 전건이 내려온다. HEAD 는 서버 페이징이 없다.
 */
export const withDefaultDateRange = (f: Rgr02FilterValues, today: string): Rgr02FilterValues =>
  f.PLN_DATE ? f : { ...f, PLN_DATE: [today, today] }

/**
 * 조회조건 세션 유지 — `MenuStorage.menuOnSession.setSearch` + URL 에 남기는 모양(gr02 `searchQuery.ts` 방식).
 * `Utils.qsStringify`(comma) → `useParsedQuery`(comma) 왕복이라 값이 하나인 목록은 문자열로 돌아온다.
 */
export interface Rgr02SearchQuery {
  serviceTypes?: string[]
  shipperNames?: string[]
  ordOptNos?: string[]
  ordNos?: string[]
  plnDateFrom?: string
  plnDateTo?: string
  ordType?: string
  actDate?: string
}

/**
 * 목록 값은 하나씩 인코딩해 싣는다 — `Utils.qsStringify` 는 comma 로 잇고 인코딩하지 않아(`encode: false`) 값 안의
 * 쉼표·`&` 가 구분자로 읽힌다(화주명 `A,B` → `A`·`B`). `useParsedQuery` 의 qs.parse 는 쉼표로 가른 뒤 %xx 를 풀어 준다(gr02 keyword 준용).
 */
const encodeList = (v?: string[]): string[] | undefined => pickList(v)?.map(encodeURIComponent)

export const toSearchQuery = (f: Rgr02FilterValues): Rgr02SearchQuery => ({
  serviceTypes: encodeList(f.service_type),
  shipperNames: encodeList(f.shipper_name),
  ordOptNos: encodeList(f.ORD_OPT_NO),
  ordNos: encodeList(f.ORD_NO),
  plnDateFrom: f.PLN_DATE?.[0],
  plnDateTo: f.PLN_DATE?.[1],
  ordType: f.ORD_TYPE || undefined,
  actDate: f.ACT_DATE || undefined,
})

/** 조작된 URL(객체·중첩 배열)은 버리고 문자열 목록만 남긴다. */
const asList = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' || typeof x === 'number').map(String)
  return typeof v === 'string' && v.trim() ? [v.trim()] : []
}
const asDate = (v: unknown): string | undefined => (typeof v === 'string' && DATE_RE.test(v) ? v : undefined)

/** URL 쿼리 → 복원 필터. 화주 조건이 없으면 복원하지 않는다 — 그대로 조회하면 BE 가 400 이다. */
export const fromSearchQuery = (q: Record<string, unknown>): Rgr02FilterValues | undefined => {
  const serviceTypes = asList(q.serviceTypes)
  const shipperNames = asList(q.shipperNames)
  if (!serviceTypes.length && !shipperNames.length) return undefined
  const from = asDate(q.plnDateFrom)
  const to = asDate(q.plnDateTo)
  return {
    service_type: serviceTypes.length ? serviceTypes : [''],
    shipper_name: shipperNames.length ? shipperNames : [''],
    PLN_DATE: from && to ? [from, to] : undefined,
    ORD_OPT_NO: pickList(asList(q.ordOptNos)),
    ORD_NO: pickList(asList(q.ordNos)),
    ORD_TYPE: typeof q.ordType === 'string' ? q.ordType : undefined,
    ACT_DATE: asDate(q.actDate),
  }
}

/** 레거시 날짜 원문 → 표시값. `yyyyMMdd` → `YYYY-MM-DD`, `yyyyMMddHHmmss` → `YYYY-MM-DD HH:mm`. 그 밖은 그대로. */
export const formatLegacyDate = (v: string | null | undefined): string => {
  if (!v) return ''
  if (/^\d{8}$/.test(v)) return `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`
  if (/^\d{14}$/.test(v)) return `${formatLegacyDate(v.slice(0, 8))} ${v.slice(8, 10)}:${v.slice(10, 12)}`
  return v
}

/** 표 행 — 생성본 컬럼 dataIndex 로 옮긴 표시값 + 동작에 쓰는 원본(`raw`). */
export type HeadRow = Record<string, unknown> & { key: string; raw: ReturnReportSummary }
export type ItemRow = Record<string, unknown> & { key: string; raw: ReturnReportItem }

/** HEAD 행 키 — BE 가 (SPR_NM, CLS_DATE, CUT_SEQ, ORD_OPT_NO) 로 묶으므로 이 조합이 유일하다. */
export const toHeadRow = (s: ReturnReportSummary): HeadRow => ({
  key: [s.sprNm, s.clsDate, s.cutSeq, s.ordOptNo].join('|'),
  raw: s,
  service_type: s.serviceType,
  shipper_name: s.shipperName,
  SPR_NM: s.sprNm,
  ORD_OPT_NO: s.ordOptNo,
  ORD_NO: s.ordNo,
  PLN_DATE: formatLegacyDate(s.plnDate),
  ORD_STATUS: s.ordStatus,
  ORD_QTY: s.ordQty,
  ACT_QTY: s.actQty,
  WH_NM: s.whNm,
  ORD_TYPE: s.ordType,
  TRS_TYPE: s.trsType,
  PROC_NM: s.procNm,
  INS_DATE: formatLegacyDate(s.insDate),
  INS_BY: s.insBy,
  UPD_DATE: formatLegacyDate(s.updDate),
  UPD_BY: s.updBy,
})

export const toItemRow = (i: ReturnReportItem, index: number, media?: { atchFileCount: number; videoCount: number }): ItemRow => ({
  key: i.id != null ? String(i.id) : `${i.ordOptNo}-${index}`,
  raw: i,
  service_type: i.serviceType,
  shipper_name: i.shipperName,
  SPR_NM: i.sprNm,
  ORD_NO: i.ordNo,
  ORD_OPT_NO: i.ordOptNo,
  BRD_NM: i.brdNm,
  GDS_CD: i.gdsCd,
  BARCODE: i.barcode,
  ORD_STATUS: i.ordStatus,
  ORD_TYPE: i.ordType,
  PLN_DATE: formatLegacyDate(i.plnDate),
  ORD_QTY: i.ordQty,
  ACT_DATE: formatLegacyDate(i.actDate),
  ACT_QTY: i.actQty,
  // 단위코드는 UI_GRREPORT_DETAIL 에 물리 컬럼이 없다(BE ReturnReportItemExcelDto 도 빈 값, 1.0 그리드도 빈 칸).
  ULD_TYPE: NOT_PROVIDED,
  TRS_TYPE: i.trsType,
  TRS_TYPE_DETAIL: i.trsTypeDetail,
  TRS_LSP_NM: i.trsLspNm,
  DRV_NAME: i.drvName,
  TRS_NUMBER: i.trsNumber,
  TRS_DEPT_DT: formatLegacyDate(i.trsDeptDt),
  TRS_ARRV_DT: formatLegacyDate(i.trsArrvDt),
  REF_NO: i.refNo,
  REF_OPT_NO: i.refOptNo,
  PROC_NM: i.procNm ?? '',
  LOT_ATTR5: i.lotAttr5,
  ATCH_FILE: media?.atchFileCount ?? 0,
  VIDEO: media?.videoCount ?? 0,
  INS_DATE: formatLegacyDate(i.insDate),
  INS_BY: i.insBy,
  UPD_DATE: formatLegacyDate(i.updDate),
  UPD_BY: i.updBy,
  WH_NM: i.whNm,
})

/** HEAD 행 → ITEM 조회 조건. 키가 하나라도 없으면 null(BE 필수 파라미터라 400). 화주는 그 행의 화주로 좁힌다. */
export const toItemReq = (s: ReturnReportSummary): GetReturnReportItemsReq | null => {
  if (!s.ordOptNo || !s.clsDate || s.cutSeq == null) return null
  return {
    ordOptNo: s.ordOptNo,
    clsDate: s.clsDate,
    cutSeq: s.cutSeq,
    shipper: s.shipperName ? [s.shipperName] : undefined,
    logisticsServiceType: s.serviceType ? [s.serviceType] : undefined,
  }
}

/** 원복·재전송(1행) 가드. 막으면 안내 문구, 통과면 null. 원복의 상태 검사는 BE 가 한다(1.0 도 FE 검사 없음). */
export const summaryActionBlockReason = (s: ReturnReportSummary, action: 'restore' | 'resend'): string | null => {
  if (s.summaryId == null || !s.sprNm) return MESSAGES.summaryMissing
  if (action === 'resend' && s.ordStatus !== RESENDABLE_STATUS) return MESSAGES.resendNotAllowed
  return null
}

/** 원복·재전송 실패 문구. 502 는 data.items 에 결과가 실려 온다 — UNKNOWN 이 있으면 결과 미확정 안내가 우선한다. */
export const actionFailureMessage = (error: unknown): string => {
  const body = (error as { response?: { data?: { meta?: { message?: string }; data?: ReturnInboundActionResult } } })?.response?.data
  if (body?.data?.items?.some((item) => item.outcome === 'UNKNOWN')) return MESSAGES.actionUnknown
  return body?.meta?.message || MESSAGES.actionFailed
}

/** 압축 파일 비밀번호 — 출고(GI37·GI38)도 같이 써서 공용으로 옮겼다(LOGISPRD-15797). 반품 화면은 이 경로로 그대로 읽는다. */
export { isValidDownloadPassword } from '@/utils/downloadPassword'

/** 목록 검색 — 받아 둔 행 안에서 표시값 어느 칸이든 검색어를 포함하면 남긴다(`raw`·행 키는 보지 않는다 — 화면에 없는 값으로 남기지 않게). 뷰의 목록 검색과 같게 대소문자를 가리지 않는다. */
export const filterRowsByKeyword = <T extends object>(rows: T[], keyword: string): T[] => {
  const kw = keyword.trim().toLowerCase()
  if (!kw) return rows
  return rows.filter((row) => Object.entries(row).some(([k, v]) => k !== 'raw' && k !== 'key' && v != null && String(v).toLowerCase().includes(kw)))
}
